import React, { useState, useEffect } from "react"
import {
  List,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
} from "@mui/material"
import MuiDrawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { ExpandLess, ExpandMore } from "@mui/icons-material"
import { styled, useTheme } from "@mui/material/styles"
import DynamicIcon from "../utils/DynamicIcon"
import LinearProgress from "@mui/material/LinearProgress"
import api from "../services/AuthApi"
import { useLocation, useNavigate } from "react-router-dom"
import { ReactComponent as WhiteLogo } from "../../../resources/WhiteLogoNoBackground.svg"

const drawerWidth = 240
const currentYear = new Date().getFullYear()

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "dopen",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}))

// Selected menu item

const Sidebar = ({ handleDrawerClose, dopen }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [open, setOpen] = useState({})
  const [menuData, setMenuData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const theme = useTheme()

  let menuConfig = {
    headers: {
      "Sort-Order": "asc",
      "Sort-Field": "mainMenuId",
    },
  }

  useEffect(() => {
    setIsLoading(true)
    api
      .get("/menus", menuConfig)
      .then((res) => {
        setMenuData(
          res.data?.dataList[0].menuList ? res.data?.dataList[0].menuList : []
        )
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)
        console.log(error)
      })
  }, [])

  const menuPath = (item) => typeof item?.route === "string" && item.route
    ? ("/app/" + item.route.replace(/^\/+/, "")).replace(/\/+$/, "")
    : null
  const matchesRoute = (item) => {
    const path = menuPath(item)
    const current = pathname.replace(/\/+$/, "")
    return path && (current === path || (path !== "/app" && current.startsWith(path + "/")))
  }
  const containsActiveRoute = (item) => matchesRoute(item) ||
    [...(item.subMenuList || []), ...(item.miniMenuList || []), ...(item.funcList || [])].some(containsActiveRoute)

  const handleClick = (index, menuItem) => {
    setOpen((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }))
    const path = menuPath(menuItem)
    if (path) navigate(path)
  }

  const renderMenuItems = (menuItems, parentIndex = "") => {
    return menuItems.map((menuItem, index) => {
      const hasSubMenu = menuItem.subMenuList && menuItem.subMenuList.length > 0
      const itemIndex = parentIndex ? `${parentIndex}-${index}` : `${index}`

      return (
        <React.Fragment key={itemIndex}>
          <ListItemButton
            aria-current={matchesRoute(menuItem) ? "page" : undefined}
            onClick={() => handleClick(itemIndex, menuItem)}
            sx={{
              minHeight: 75,
              justifyContent: dopen ? "initial" : "center",
              px: 2.5,
              bgcolor:
                containsActiveRoute(menuItem) ? "#1D888C" : "#116c6f",
              color: "#fff",
              "&:hover": {
                bgcolor: "#1D888C",
              },
            }}>
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: dopen ? 3 : "auto",
                justifyContent: "center",
                color: "#fff",
              }}>
              <DynamicIcon iconName={menuItem.icon} />
            </ListItemIcon>
            <ListItemText
              primary={menuItem.mainMenuName}
              sx={{ opacity: dopen ? 1 : 0 }}
            />
            {hasSubMenu && (open[itemIndex] ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
          {hasSubMenu && (
            <Collapse in={open[itemIndex] ?? containsActiveRoute(menuItem)} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderSubMenuItems(menuItem.subMenuList, itemIndex)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      )
    })
  }

  const renderSubMenuItems = (subMenuItems, parentIndex) => {
    return subMenuItems.map((subMenuItem, index) => {
      const hasMiniMenu =
        subMenuItem.miniMenuList && subMenuItem.miniMenuList.length > 0
      const itemIndex = `${parentIndex}-${index}`

      return (
        <React.Fragment key={itemIndex}>
          <ListItemButton
            onClick={() => handleClick(itemIndex, subMenuItem)}
            aria-current={matchesRoute(subMenuItem) ? "page" : undefined}
            sx={{ pl: 4, color: "#fff", bgcolor: containsActiveRoute(subMenuItem) ? "#1D888C" : undefined }}>
            <ListItemIcon sx={{ color: "#fff" }}>
              <DynamicIcon iconName={subMenuItem.icon} />
            </ListItemIcon>
            <ListItemText primary={subMenuItem.subMenuName} />
            {hasMiniMenu && (open[itemIndex] ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
          {hasMiniMenu && (
            <Collapse in={open[itemIndex] ?? containsActiveRoute(subMenuItem)} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderMiniMenuItems(subMenuItem.miniMenuList, itemIndex)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      )
    })
  }

  const renderMiniMenuItems = (miniMenuItems, parentIndex) => {
    return miniMenuItems.map((miniMenuItem, index) => {
      const hasFunctions =
        miniMenuItem.funcList && miniMenuItem.funcList.length > 0
      const itemIndex = `${parentIndex}-${index}`

      return (
        <React.Fragment key={itemIndex}>
          <ListItemButton
            onClick={() => handleClick(itemIndex, miniMenuItem)}
            aria-current={matchesRoute(miniMenuItem) ? "page" : undefined}
            sx={{ pl: 8, color: "#fff", bgcolor: containsActiveRoute(miniMenuItem) ? "#1D888C" : undefined }}>
            <ListItemIcon sx={{ color: "#fff" }}>
              <DynamicIcon iconName={miniMenuItem.icon} />
            </ListItemIcon>
            <ListItemText primary={miniMenuItem.miniMenuName} />
            {hasFunctions &&
              (open[itemIndex] ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
          {hasFunctions && (
            <Collapse in={open[itemIndex] ?? containsActiveRoute(miniMenuItem)} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderFunctions(miniMenuItem.funcList, itemIndex)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      )
    })
  }

  const renderFunctions = (functions, parentIndex) => {
    return functions.map((func, index) => {
      const itemIndex = `${parentIndex}-${index}`

      return (
        <React.Fragment key={itemIndex}>
          <ListItemButton
            onClick={() => handleClick(itemIndex, func)}
            aria-current={matchesRoute(func) ? "page" : undefined}
            sx={{ pl: 12, color: "#fff", bgcolor: matchesRoute(func) ? "#1D888C" : undefined }}>
            <ListItemIcon>
              <DynamicIcon iconName={func.icon} />
            </ListItemIcon>
            <ListItemText primary={func.funcName} />
          </ListItemButton>
        </React.Fragment>
      )
    })
  }

  return (
    <div>
      <Drawer
        variant="permanent"
        open={dopen}
        role={"Drawer"}
        PaperProps={{ style: { border: 0, backgroundColor: "#116c6f" } }}>
        <DrawerHeader
          role={"DrawerHeader"}
          sx={{ bgcolor: "#116c6f", position: "relative" }}>
          <WhiteLogo />
          <IconButton onClick={handleDrawerClose} aria-label="close drawer">
            {theme.direction === "rtl" ? (
              <ChevronRightIcon sx={{ color: "#e8f3f3" }} />
            ) : (
              <ChevronLeftIcon sx={{ color: "#e8f3f3" }} />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        {isLoading ? (
          <LinearProgress />
        ) : (
          <List
            component="nav">
            {renderMenuItems(menuData)}
          </List>
        )}
        {dopen && (
          <p
            style={{
              position: "absolute",
              color: "#bbb",
              paddingLeft: "25px",
              bottom: "10px",
              fontSize: "12px",
            }}>
            {process.env.REACT_APP_VERSION} <br /> {currentYear} All rights
            reserved
          </p>
        )}
      </Drawer>
    </div>
  )
}

export default Sidebar
